import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import ActionButton from 'Components/ActionButton';
import { ClearIconStyled } from '..';
import { epilepsySyndromeOptionsWithHeaders } from 'Routes/Diagnosis/utils/definitions';
import type { IExtendedSearchField } from '../ExtendedSearchField';

type TSyndromeValue = NonNullable<ISyndrome['syndrome']>;

type TRadioObjectEntry = { type: 'option' | 'header'; level: number; value?: string | number; text?: string };

// Each option carries a composite group key "level0Key|||level1Key" so that
// renderGroup can show both hierarchy levels independently.
const GROUP_SEP = '|||';

const syndromeOptions = (() => {
  const result: Array<{ syndrome: TSyndromeValue; group: string }> = [];
  let level0Key = '';
  let level1Key = '';
  for (const entry of epilepsySyndromeOptionsWithHeaders ?? []) {
    if (typeof entry !== 'object') continue;
    const e = entry as TRadioObjectEntry;
    if (e.type === 'header') {
      if (e.level === 0) {
        level0Key = e.text ?? '';
        level1Key = '';
      } else {
        level1Key = e.text ?? '';
      }
    } else if (e.type === 'option' && e.value) {
      result.push({ syndrome: e.value as TSyndromeValue, group: level0Key + GROUP_SEP + level1Key });
    }
  }
  return result;
})();

const SyndromeField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const criteria: Array<TSyndromeValue | null | undefined> = Array.isArray(searchFields.syndrome)
    ? searchFields.syndrome
    : [undefined];

  const onChangeCriteria = (i: number) => (values: TOnChangeValues) => {
    const selected = values['syndrome'] as { syndrome: TSyndromeValue; group: string } | undefined;

    const newCriteria: Array<TSyndromeValue | null | undefined> = structuredClone(
      Array.isArray(searchFields.syndrome) && searchFields.syndrome.length > 0 ? searchFields.syndrome : [undefined],
    );
    newCriteria[i] = selected?.syndrome ?? undefined;
    onChange({ syndrome: newCriteria });
  };

  return (
    <React.Fragment>
      {criteria.map((item, i, arr) => {
        // Track the last-rendered level-0 header within this dropdown render.
        // renderGroup is called synchronously in list order, so a local variable is safe.
        let lastLevel0 = '';

        const renderGroup = (params: { key: string | number; group: string; children?: React.ReactNode }) => {
          const sepIdx = params.group.indexOf(GROUP_SEP);
          const level0Key = params.group.slice(0, sepIdx);
          const level1Key = params.group.slice(sepIdx + GROUP_SEP.length);
          const isNewLevel0 = level0Key !== lastLevel0;
          if (isNewLevel0) lastLevel0 = level0Key;

          const level0Label = level0Key
            ? formatMessage({ id: `diagnosis.epilepsy.syndrome.headers.${level0Key}` })
            : '';
          const level1Label = level1Key
            ? formatMessage({ id: `diagnosis.epilepsy.syndrome.headers.${level1Key}` })
            : '';

          return (
            <li key={params.key}>
              {isNewLevel0 && level0Label && (
                <div
                  style={{
                    fontWeight: 600,
                    padding: '0.5rem 1rem 0.2rem',
                    // color: colors.tertiaryText,
                    // fontSize: '1.4rem',
                  }}
                >
                  {level0Label}
                </div>
              )}
              {level1Label && (
                <div
                  style={{
                    fontWeight: 600,
                    padding: '0.2rem 1rem 0.2rem 2rem',
                    // color: colors.tertiaryText,
                    fontSize: '1.4rem',
                  }}
                >
                  {level1Label}
                </div>
              )}
              <ul style={{ margin: 0, padding: '0 0 0 1rem' }}>{params.children}</ul>
            </li>
          );
        };

        return (
          <React.Fragment key={`syndromeCriteria${i}`}>
            <div
              style={{
                backgroundColor: colors.lightestGray,
                padding: '1rem',
                marginBottom: i === arr.length - 1 ? '1rem' : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                  {fm('extendedSearch.searchCriteriaTitle')}
                  {` ${i + 1}`}
                </div>
                {arr.length > 1 && (
                  <ClearIconStyled
                    onClick={() => {
                      const filtered = arr.filter((_, index) => index !== i);
                      onChange({ syndrome: filtered.length > 0 ? filtered : 'noCropping' });
                    }}
                  />
                )}
              </div>
              <InputHandler
                type="AutoCompleteSelect"
                name="syndrome"
                editing={true}
                formData={{
                  onChange: onChangeCriteria(i),
                  document: { syndrome: item ? { syndrome: item } : null },
                }}
                options={syndromeOptions}
                placeholder="extendedSearch.syndromePlaceholder"
                width={30}
                getOptionLabel={(o: { syndrome: TSyndromeValue; group: string }) =>
                  o.syndrome ? formatMessage({ id: `diagnosis.epilepsy.syndrome.opts.${o.syndrome}` }) : ''
                }
                renderOption={(o: { syndrome: TSyndromeValue; group: string }) =>
                  o.syndrome ? (
                    <div>{formatMessage({ id: `diagnosis.epilepsy.syndrome.opts.${o.syndrome}` })}</div>
                  ) : null
                }
                groupID="group"
                groupBy={(option: Record<string, string>) => option['group'] ?? ''}
                renderGroup={renderGroup}
              />
            </div>
            {arr.length > 1 && i < arr.length - 1 && (
              <div style={{ padding: '1rem', textTransform: 'uppercase' }}>{fm('general.or')}</div>
            )}
          </React.Fragment>
        );
      })}
      <div style={{ marginTop: '2rem', justifyContent: 'center' }}>
        <ActionButton
          text={'extendedSearch.addSearchCriteria'}
          onClick={() => {
            const newCriteria = structuredClone(
              Array.isArray(searchFields.syndrome) && searchFields.syndrome.length > 0
                ? searchFields.syndrome
                : [undefined],
            );
            newCriteria.push(undefined);
            onChange({ syndrome: newCriteria });
          }}
          width={16}
          height={3}
          fontSize={16}
        />
      </div>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default SyndromeField;
